import { FC, useState } from 'react';
import styled from 'styled-components';
import { differenceInSeconds } from 'date-fns';
import {
  alpha,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import {
  VsTransferEvent,
  useAcceptTransferRequest,
  useDeclineTransferRequest,
  useGetHeroConversationById,
} from '@vs-online-only-app/state';
import {
  CountdownTimer,
  truncateText,
  UserAvatar,
} from '@vs-online-only-app/ui';
import { TransferRequestBadge } from './TransferRequestBadge';
import { AnimatedBaseConversationCard } from './StyledConversationCards';
import { ParticipantName } from './ParticipantName';

const Body = styled(Stack)`
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.palette.requestNotification.main};
`;

const CurrentExpertNoteCard = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  background: ${({ theme }) => alpha(theme.palette.background.paper, 0.4)};
  padding: ${({ theme }) => theme.spacing(1.5)};
  gap: ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => theme.shape.borderRadius * 2}px;
`;

type Props = {
  event: VsTransferEvent;
};

export const TransferRequestCard: FC<Props> = ({ event }) => {
  const declineTransferRequestMutation = useDeclineTransferRequest();
  const acceptTransferRequestMutation = useAcceptTransferRequest();
  const [isTransferAcceptanceInProgress, setIsTransferAcceptanceInProgress] =
    useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const { conversation: conversationId, expiration } = event;

  const { data: conversation } = useGetHeroConversationById(conversationId);

  if (!conversation) {
    return null;
  }

  const {
    conversationStarter: { firstName: shopperFirstName },
    currentAgent: {
      firstName: currentAgentFirstName,
      image: currentAgentAvatarUrl,
    },
  } = conversation;

  if (!isVisible) {
    return null;
  }

  const isShopperNameTruncated =
    truncateText(shopperFirstName) === shopperFirstName;

  return (
    <AnimatedBaseConversationCard elevation={1}>
      <Body>
        <Stack direction="row" gap={2}>
          <Stack gap={1}>
            <TransferRequestBadge />
            <Typography>
              <strong>
                <ParticipantName name={currentAgentFirstName} />
              </strong>{' '}
              wants you to chat with{' '}
              <strong>
                <ParticipantName name={shopperFirstName} />
                {isShopperNameTruncated ? '...' : ''}
              </strong>
            </Typography>
          </Stack>
          <CountdownTimer
            initialDurationSeconds={120}
            remainingDurationSeconds={
              isTransferAcceptanceInProgress
                ? 0
                : differenceInSeconds(new Date(expiration), new Date(), {
                    roundingMethod: 'ceil',
                  })
            }
            isPaused={isTransferAcceptanceInProgress}
            onTimeout={() => setIsVisible(false)}
          />
        </Stack>
        <CurrentExpertNoteCard>
          <UserAvatar imageSrc={currentAgentAvatarUrl} />
          <Stack>
            <Typography variant="body2" fontWeight="bold">
              <ParticipantName name={currentAgentFirstName} /> says:
            </Typography>
            <Typography variant="body2">{event.note}</Typography>
          </Stack>
        </CurrentExpertNoteCard>
        <Stack direction="row" gap={1}>
          <Button
            startIcon={isTransferAcceptanceInProgress ? null : <Check />}
            variant="contained"
            onClick={() => {
              setIsTransferAcceptanceInProgress(true);
              acceptTransferRequestMutation.mutateAsync(event.id);
            }}
            fullWidth
          >
            {isTransferAcceptanceInProgress ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              'Accept'
            )}
          </Button>
          <Button
            variant="outlined"
            disabled={isTransferAcceptanceInProgress}
            onClick={() => {
              declineTransferRequestMutation.mutateAsync(event.id);
              setIsVisible(false);
            }}
            fullWidth
          >
            Decline
          </Button>
        </Stack>
      </Body>
    </AnimatedBaseConversationCard>
  );
};
